const HANDLE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const PLACEHOLDER = 'https://cdn.shopify.com/s/files/1/0494/7918/8636/files/placeholder_productcard.png?v=1769023949';
const NATIVE_ACCESS = 'https://www.native-instruments.com/en/specials/native-access-2/';
const STORAGE_PREFIX = 'mw_downloads_catalog_v1:';
const LIMIT = 3 * 1024 * 1024;

export function changelogURL(product) {
  const helpPath = product.productType === 'plugin' ? 'plugins-release-notes' : 'drum-libraries-release-notes';
  const anchor = product.changelogAnchor || product.productHandle;
  return `https://support.mixwave.com/help/${helpPath}#${encodeURIComponent(anchor)}`;
}

export function safeURL(value, kind = 'resource') {
  if (typeof value !== 'string' || !value.trim()) return '';
  try {
    if (/[\\\u0000-\u001f]/.test(value)) return '';
    const rawPath = value.replace(/^(?:https?:)?\/\/[^/]+/i, '').split(/[?#]/)[0];
    if (rawPath.split('/').some(part => ['.', '..'].includes(decodeURIComponent(part)))) return '';
    const url = new URL(value.startsWith('//') ? `https:${value}` : value);
    if (url.protocol !== 'https:' || url.username || url.password || url.port) return '';
    if (kind === 'installer') {
      const host = url.hostname.toLowerCase();
      const path = decodeURIComponent(url.pathname);
      const own = host === 'cdn.mixwave.com' || host === 'dr3xzrcqleaw9.cloudfront.net';
      const native = host === 'www.native-instruments.com';
      if (!own && !native) return '';
      if (/(?:^|\/)(?:beta|!?-?public-beta)(?:\/|$)/i.test(path) || /[\\\u0000-\u001f]/.test(path)) return '';
      if (own && (!/^\/plugins\//.test(path) || !/\.(?:pkg|exe)$/i.test(path))) return '';
      if (native && (!/^\/fileadmin\/downloads\//.test(path) || !/\.(?:dmg|exe)$/i.test(path))) return '';
      if (url.search || url.hash) return '';
    } else if (kind === 'image' && url.hostname !== 'cdn.shopify.com') return '';
    else if (kind === 'resource' && !['support.mixwave.com', 'cdn.shopify.com'].includes(url.hostname)) return '';
    return url.href;
  } catch { return ''; }
}

function dateOnly(value) {
  if (value === '' || value == null) return '';
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}(?:$|T)/.test(value)) throw new Error('Invalid release date');
  const day = value.slice(0, 10);
  const date = new Date(`${day}T00:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== day) throw new Error('Invalid release date');
  if (value.length > 10 && !Number.isFinite(Date.parse(value))) throw new Error('Invalid release date');
  return day;
}

function text(value, maximum = 500) {
  if (value == null) return '';
  if (typeof value !== 'string' || value.length > maximum) throw new Error('Invalid product text');
  return value;
}

function checkedURL(value, kind) {
  const input = text(value, 3000);
  if (!input) return '';
  const result = safeURL(input, kind);
  if (!result) throw new Error('Invalid product address');
  return result;
}

export function artworkPalette(value) {
  if (!Array.isArray(value) || value.length < 2 || value.length > 3 ||
      value.some(color => typeof color !== 'string' || !/^#[0-9a-f]{6}$/i.test(color))) return [];
  const colors = value.map(color => color.toUpperCase());
  if (new Set(colors).size < 2) return [];
  return colors.length === 2 ? [...colors, colors[0]] : colors;
}

export function validateDownloadsFeed(feed) {
  if (!feed || feed.schemaVersion !== 1 || !Array.isArray(feed.products) || feed.products.length > 2000 ||
      typeof feed.servedAt !== 'string' || !Number.isFinite(Date.parse(feed.servedAt)) ||
      !((typeof feed.sourceRevision === 'string' && feed.sourceRevision.length > 0) ||
        (Number.isSafeInteger(feed.sourceRevision) && feed.sourceRevision >= 0))) throw new Error('Invalid downloads response');
  const handles = new Set();
  const products = feed.products.map(value => {
    if (!value || !HANDLE.test(value.productHandle) || handles.has(value.productHandle) ||
        !['plugin', 'drum-library'].includes(value.productType)) throw new Error('Invalid downloads product');
    if (value.isCollection != null && typeof value.isCollection !== 'boolean') throw new Error('Invalid collection marker');
    handles.add(value.productHandle);
    const name = text(value.productName, 300);
    if (!name.trim()) throw new Error('Missing product name');
    const children = value.children ?? [];
    if (!Array.isArray(children) || children.length > 200 || new Set(children).size !== children.length ||
        children.some(handle => !HANDLE.test(handle) || handle === value.productHandle)) throw new Error('Invalid collection members');
    const primaryChild = value.primaryChild || '';
    if (primaryChild && (!HANDLE.test(primaryChild) || !children.includes(primaryChild))) throw new Error('Invalid primary member');
    return {
      productHandle: value.productHandle, productName: name, productType: value.productType,
      releaseDate: dateOnly(value.releaseDate), updatedDate: dateOnly(value.updatedDate),
      currentVersion: text(value.currentVersion, 80),
      platformVersions: { mac: text(value.platformVersions?.mac, 80), win: text(value.platformVersions?.win, 80) },
      platformUpdatedDates: { mac: dateOnly(value.platformUpdatedDates?.mac), win: dateOnly(value.platformUpdatedDates?.win) },
      macInstaller: checkedURL(value.macInstaller, 'installer'), pcInstaller: checkedURL(value.pcInstaller, 'installer'),
      userGuide: checkedURL(value.userGuide, 'resource'), changelogAnchor: text(value.changelogAnchor, 300),
      image: checkedURL(value.image, 'image'), artworkPalette: artworkPalette(value.artworkPalette), children: [...children], primaryChild,
      isCollection: value.isCollection === true || Object.hasOwn(value, 'children')
    };
  });
  const lookup = new Map(products.map(product => [product.productHandle, product]));
  for (const product of products) {
    if (product.children.some(handle => !handles.has(handle))) throw new Error('Missing public collection member');
    if (product.children.some(handle => lookup.get(handle).isCollection)) throw new Error('Collection member is not a public leaf');
  }
  return { schemaVersion: 1, servedAt: feed.servedAt, sourceRevision: feed.sourceRevision, products };
}

export function selectWebsiteProducts(products) {
  return products.filter(product => !product.isCollection);
}

export function filterAndSortProducts(products, { search = '', productHandles = [], category = 'all', sort = 'release', ownedOnly = false, ownedHandles = new Set() } = {}) {
  const queries = search.toLowerCase().split(',').map(query => query.trim()).filter(Boolean);
  const result = products.filter(product =>
    (productHandles.length ? productHandles.includes(product.productHandle) : !queries.length || queries.some(query =>
      product.productHandle === query || product.productName.toLowerCase().includes(query) || product.productType.toLowerCase().includes(query))) &&
    (category === 'all' || product.productType === category) && (!ownedOnly || ownedHandles.has(product.productHandle)));
  const date = value => value ? Date.parse(`${value}T00:00:00Z`) : 0;
  return result.sort((a, b) => {
    if (sort === 'alpha') return a.productName.localeCompare(b.productName);
    if (sort === 'updated') {
      if (!a.updatedDate && !b.updatedDate) return date(b.releaseDate) - date(a.releaseDate);
      if (!a.updatedDate) return 1;
      if (!b.updatedDate) return -1;
      return date(b.updatedDate) - date(a.updatedDate);
    }
    return date(b.releaseDate) - date(a.releaseDate);
  });
}

export function formatDate(value) {
  const day = dateOnly(value);
  return day ? new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '';
}

export function isRecentUpdate(value, now = new Date(), releaseDate = '') {
  if (!value) return false;
  const day = dateOnly(value);
  if (releaseDate && day <= dateOnly(releaseDate)) return false;
  const update = new Date(`${day}T00:00:00Z`);
  const cutoff = new Date(now);
  cutoff.setMonth(cutoff.getMonth() - 1);
  return update > cutoff && update <= now;
}

export function downloadPresentation(product) {
  const mac = product.platformVersions.mac || product.currentVersion;
  const win = product.platformVersions.win || product.currentVersion;
  const macDate = product.platformUpdatedDates.mac || product.updatedDate || product.releaseDate;
  const winDate = product.platformUpdatedDates.win || product.updatedDate || product.releaseDate;
  const separate = mac !== win || macDate !== winDate;
  return { separate, mac, win, macDate, winDate,
    summary: separate ? '' : [mac ? `Version ${mac}` : '', formatDate(macDate)].filter(Boolean).join(' · ') };
}

export function loginURL(href) {
  const location = new URL(href);
  const returnTo = `${location.pathname}${location.search}${location.hash}`;
  return `/customer_authentication/login?return_to=${encodeURIComponent(returnTo)}`;
}

function storageRead(storage, key) { try { return storage?.getItem(STORAGE_PREFIX + key); } catch { return null; } }
function storageWrite(storage, key, value) { try { storage?.setItem(STORAGE_PREFIX + key, value); } catch {} }
function storageRemove(storage, key) { try { storage?.removeItem(STORAGE_PREFIX + key); } catch {} }

export function createDownloadsCatalog(root, options = {}) {
  const doc = root.ownerDocument;
  const view = doc.defaultView;
  const fetcher = options.fetch || view.fetch.bind(view);
  const location = options.location || view.location;
  let linkedHandles = [...new Set((new URL(location.href).searchParams.get('product') || '')
    .split(',').map(handle => handle.trim().toLowerCase()).filter(Boolean))];
  let local, session;
  try { local = options.localStorage || view.localStorage; session = options.sessionStorage || view.sessionStorage; } catch {}
  const find = selector => root.querySelector(selector);
  const grid = find('[data-grid]'), status = find('[data-status]'), statusMessage = find('[data-status-message]');
  const retry = find('[data-retry]'), empty = find('[data-empty]'), emptyMessage = find('[data-empty-message]');
  const search = find('[data-control="search"]'), category = find('[data-control="category"]'), sort = find('[data-control="sort"]');
  const clearSearch = find('[data-control="clear-search"]');
  const owned = find('[data-control="owned"]'), drawer = find('[data-control="drawer"]');
  const ownershipStatus = find('[data-ownership-status]'), ownershipMessage = find('[data-ownership-message]');
  const ownershipRetry = find('[data-ownership-retry]');
  const loggedIn = root.dataset.loggedIn === 'true';
  let products = [], catalogProducts = [], ownedHandles = new Set(), imageCache = new Map(), controller, generation = 0, destroyed = false;
  let ownershipState = 'idle', ownershipController, catalogReady = false;

  const node = (tag, className, value) => {
    const element = doc.createElement(tag);
    if (className) element.className = className;
    if (value != null) element.textContent = value;
    return element;
  };
  const button = (label, href, className = 'button-main', external = false) => {
    const link = node(href ? 'a' : 'span', className, label);
    if (href) {
      link.href = href;
      if (external) { link.target = '_blank'; link.rel = 'noopener noreferrer'; }
      else link.setAttribute('download', '');
    } else link.setAttribute('aria-disabled', 'true');
    return link;
  };
  const renderDownloads = product => {
    const wrapper = node('div', 'downloads');
    wrapper.append(node('p', 'title', 'Installers'));
    const display = downloadPresentation(product);
    if (display.summary) wrapper.append(node('p', 'version', display.summary));
    else if (product.productType === 'drum-library') {
      for (const [platform, name] of [['mac', 'macOS'], ['win', 'Windows']]) {
        const date = display[`${platform}Date`];
        wrapper.append(node('p', 'version', [name, display[platform] ? `Version ${display[platform]}` : '',
          formatDate(date)].filter(Boolean).join(' · ')));
      }
    }
    const buttons = node('div', `download-buttons${product.productType === 'drum-library' ? ' downloads-native' : ''}`);
    if (product.productType === 'drum-library') buttons.append(button('Open Native Access', NATIVE_ACCESS, 'button-main', true));
    else for (const [platform, name, address] of [['mac', 'macOS', product.macInstaller], ['win', 'Windows', product.pcInstaller]]) {
      const group = node('div', 'downloads-platform');
      if (display.separate) {
        const version = display[platform], date = display[`${platform}Date`];
        group.append(node('p', 'version', [version ? `Version ${version}` : '', formatDate(date)].filter(Boolean).join(' · ')));
      }
      group.append(button(address ? name : `${name} unavailable`, address));
      buttons.append(group);
    }
    wrapper.append(buttons);
    return wrapper;
  };
  const card = product => {
    const article = node('article', 'product-card');
    article.dataset.productHandle = product.productHandle;
    const imageContainer = node('div', 'product-image-container');
    const image = node('div', 'product-image');
    image.dataset.productImage = product.productHandle;
    image.style.setProperty('--product-artwork-image', `url(${JSON.stringify(product.image || imageCache.get(product.productHandle) || PLACEHOLDER)})`);
    if (product.image && new URL(product.image).pathname !== new URL(PLACEHOLDER).pathname) {
      product.artworkPalette.forEach((color, index) => image.style.setProperty(`--product-art-color-${index + 1}`, color));
    }
    imageContainer.append(image);
    if (isRecentUpdate(product.updatedDate, new Date(), product.releaseDate)) imageContainer.append(node('div', 'download-update-pill', 'NEW UPDATE'));
    const info = node('div', 'product-info');
    const heading = node('div', 'product-heading');
    const resources = node('details', 'product-resources');
    const toggle = node('summary', 'resources-toggle');
    toggle.setAttribute('aria-label', `Resources for ${product.productName}`);
    const dots = node('span', '', '•••'); dots.setAttribute('aria-hidden', 'true'); toggle.append(dots);
    const links = node('div', 'resources-links');
    if (product.userGuide) links.append(button('User Guide', product.userGuide, 'resource-link', true));
    links.append(button('Release Notes', changelogURL(product), 'resource-link', true));
    resources.append(toggle, links);
    heading.append(node('h2', '', product.productName), resources);
    info.append(heading, renderDownloads(product));
    article.append(imageContainer, info);
    return article;
  };
  const render = () => {
    clearSearch.hidden = !search.value.length && !linkedHandles.length;
    if (!catalogReady) return;
    const checkingOwnership = owned.checked && ownershipState !== 'ready';
    ownershipStatus.hidden = !checkingOwnership;
    ownershipRetry.hidden = ownershipState !== 'failed';
    ownershipMessage.textContent = ownershipState === 'failed'
      ? 'Owned products could not be checked. Try again, or turn off Show Owned to browse all downloads.'
      : 'Checking owned products…';
    grid.setAttribute('aria-busy', String(checkingOwnership && ownershipState !== 'failed'));
    if (checkingOwnership) { grid.replaceChildren(); empty.hidden = true; return; }
    const visible = filterAndSortProducts(products, { search: search.value, productHandles: linkedHandles,
      category: category.value, sort: sort.value, ownedOnly: owned.checked, ownedHandles });
    grid.replaceChildren(...visible.map(card));
    empty.hidden = !!visible.length;
    if (!visible.length) emptyMessage.textContent = search.value.trim() ? `No products found for “${search.value.trim()}”.` :
      owned.checked ? 'No owned products found. Try turning off Show Owned.' : category.value !== 'all' ?
      `No products found in ${category.value === 'plugin' ? 'Plugins' : 'Drum Libraries'}.` : 'No products found.';
  };
  const checkOwnership = async () => {
    if (!loggedIn || !catalogReady || ownershipState === 'loading' || ownershipState === 'ready') return;
    ownershipController?.abort(); ownershipController = new AbortController();
    const activeController = ownershipController, currentGeneration = generation;
    ownershipState = 'loading'; render();
    const timer = view.setTimeout(() => activeController.abort(), 20000);
    try {
      const { loadShopifyOwnership, ownedProductHandles } = await import(root.dataset.ownershipModule);
      if (generation !== currentGeneration || destroyed) return;
      const result = await loadShopifyOwnership(catalogProducts, { fetch: fetcher, location,
        searchPath: root.dataset.ownershipSearch, signal: activeController.signal,
        parseDocument: value => new view.DOMParser().parseFromString(value, 'text/html') });
      if (generation !== currentGeneration || destroyed) return;
      ownedHandles = ownedProductHandles(catalogProducts, result.directOwnedHandles);
      ownershipState = 'ready';
    } catch {
      if (generation !== currentGeneration || destroyed) return;
      ownedHandles.clear(); ownershipState = 'failed';
    } finally {
      view.clearTimeout(timer);
      if (generation === currentGeneration && !destroyed) render();
    }
  };
  const hydrateImages = async (currentGeneration, signal) => {
    const handles = products.filter(product => !product.image).map(product => product.productHandle);
    let index = 0;
    await Promise.all(Array.from({ length: Math.min(handles.length, 8) }, async () => {
      while (index < handles.length && generation === currentGeneration && !signal.aborted) {
        const handle = handles[index++];
        if (imageCache.has(handle)) continue;
        try {
          const response = await fetcher(`/products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin', signal });
          if (!response.ok) continue;
          const value = await response.json();
          const url = safeURL(value.featured_image || value.images?.[0], 'image');
          imageCache.set(handle, url);
          if (generation !== currentGeneration || !url) continue;
          for (const element of root.querySelectorAll('[data-product-image]')) {
            if (element.dataset.productImage === handle) element.style.setProperty('--product-artwork-image', `url(${JSON.stringify(url)})`);
          }
        } catch { if (!signal.aborted) imageCache.set(handle, ''); }
      }
    }));
  };
  const load = async () => {
    controller?.abort(); controller = new AbortController();
    ownershipController?.abort(); ownershipState = 'idle'; ownedHandles.clear(); catalogProducts = []; catalogReady = false;
    ownershipStatus.hidden = true;
    const activeController = controller, signal = controller.signal, currentGeneration = ++generation;
    status.hidden = false; retry.hidden = true; statusMessage.textContent = 'Loading downloads…';
    grid.setAttribute('aria-busy', 'true');
    const timer = view.setTimeout(() => activeController.abort(), 20000);
    try {
      const endpoint = new URL(root.dataset.endpoint, location.href);
      const localFixture = ['localhost', '127.0.0.1', '[::1]'].includes(endpoint.hostname) && endpoint.origin === new URL(location.href).origin;
      if (!localFixture && (endpoint.protocol !== 'https:' || endpoint.username || endpoint.password)) throw new Error('Invalid downloads endpoint');
      const response = await fetcher(endpoint.href, { credentials: 'omit', signal, redirect: 'error', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok || Number(response.headers.get('Content-Length')) > LIMIT) throw new Error('Downloads unavailable');
      const body = await response.text();
      if (body.length > LIMIT) throw new Error('Downloads response too large');
      const feed = validateDownloadsFeed(JSON.parse(body));
      if (generation !== currentGeneration || destroyed) return;
      catalogProducts = feed.products;
      catalogReady = true;
      products = selectWebsiteProducts(feed.products);
      if (linkedHandles.length) {
        const names = new Map(products.map(product => [product.productHandle, product.productName]));
        search.value = linkedHandles.map(handle => names.get(handle) || handle).join(', ');
      }
      root.dataset.sourceRevision = String(feed.sourceRevision);
      status.hidden = true; render();
      if (owned.checked) void checkOwnership();
      void hydrateImages(currentGeneration, signal);
    } catch {
      if (generation !== currentGeneration || destroyed) return;
      products = []; catalogProducts = []; ownedHandles.clear(); grid.replaceChildren(); empty.hidden = true; ownershipStatus.hidden = true;
      statusMessage.textContent = 'Downloads are temporarily unavailable. Please try again.'; retry.hidden = false;
    } finally {
      view.clearTimeout(timer);
      if (generation === currentGeneration && ownershipState !== 'loading') grid.setAttribute('aria-busy', 'false');
    }
  };
  let restore;
  try { restore = JSON.parse(storageRead(session, 'login') || 'null'); } catch {}
  if (restore) {
    search.value = typeof restore.search === 'string' ? restore.search : '';
    category.value = ['all', 'plugin', 'drum-library'].includes(restore.category) ? restore.category : 'all';
    sort.value = ['release', 'updated', 'alpha'].includes(restore.sort) ? restore.sort : 'release';
    owned.checked = loggedIn && !!restore.ownedOnly;
    storageRemove(session, 'login');
  } else if (linkedHandles.length) {
    category.value = 'all';
    owned.checked = false;
  } else owned.checked = loggedIn && storageRead(local, 'owned') === 'true';
  if (linkedHandles.length || restore?.controlsOpen || storageRead(local, 'drawer') === 'true') {
    root.classList.add('downloads-controls-open'); drawer.setAttribute('aria-expanded', 'true');
  }
  clearSearch.hidden = !search.value.length && !linkedHandles.length;
  const listeners = [];
  const listen = (element, event, callback) => { element.addEventListener(event, callback); listeners.push(() => element.removeEventListener(event, callback)); };
  const updateSearch = () => {
    linkedHandles = [];
    const url = new URL(location.href);
    if (url.searchParams.has('product')) {
      url.searchParams.delete('product');
      view.history.replaceState(view.history.state, '', url.href);
    }
    render();
  };
  listen(search, 'input', updateSearch);
  listen(clearSearch, 'click', () => { search.value = ''; updateSearch(); search.focus(); });
  listen(category, 'change', render); listen(sort, 'change', render);
  listen(doc, 'click', event => {
    for (const menu of root.querySelectorAll('.product-resources[open]')) {
      if (!menu.contains(event.target)) menu.open = false;
    }
  });
  listen(root, 'keydown', event => {
    if (event.key !== 'Escape') return;
    const menu = event.target.closest('.product-resources[open]');
    if (menu) { menu.open = false; menu.querySelector('summary').focus(); event.preventDefault(); }
  });
  listen(owned, 'change', () => {
    if (owned.checked && !loggedIn) {
      owned.checked = false;
      storageWrite(session, 'login', JSON.stringify({ search: search.value, category: category.value, sort: sort.value, ownedOnly: true, controlsOpen: root.classList.contains('downloads-controls-open') }));
      location.href = loginURL(location.href);
      return;
    }
    storageWrite(local, 'owned', String(owned.checked)); render();
    if (owned.checked) void checkOwnership();
  });
  listen(drawer, 'click', () => {
    const open = root.classList.toggle('downloads-controls-open');
    drawer.setAttribute('aria-expanded', String(open)); storageWrite(local, 'drawer', String(open));
  });
  listen(retry, 'click', load);
  listen(ownershipRetry, 'click', checkOwnership);
  const ready = load();
  return { ready, reload: load, destroy() { destroyed = true; controller?.abort(); ownershipController?.abort(); for (const remove of listeners) remove(); } };
}

const mounted = new WeakMap();
export function mountDownloadsCatalogs(scope = document, options = {}) {
  const roots = [...scope.querySelectorAll('[data-mw-downloads-catalog]')];
  if (scope.matches?.('[data-mw-downloads-catalog]')) roots.unshift(scope);
  return roots.map(root => {
    if (!mounted.has(root)) mounted.set(root, createDownloadsCatalog(root, options));
    return mounted.get(root);
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mountDownloadsCatalogs(), { once: true });
  else mountDownloadsCatalogs();
  document.addEventListener('shopify:section:load', event => mountDownloadsCatalogs(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const root of event.target.querySelectorAll('[data-mw-downloads-catalog]')) { mounted.get(root)?.destroy(); mounted.delete(root); }
  });
}
